import React from 'react';
import { adsConfig, canBootstrapAds, isLiveAdsOrigin, type AdsConfig } from '../lib/ads-config';
import { observeTcfConsent } from '../lib/ad-consent';
import { loadPrivacyMessage } from '../lib/ad-loader';

interface PrivacyState { adsAllowed: boolean; settingsReady: boolean; openSettings: () => void; }
const PrivacyContext = React.createContext<PrivacyState>({ adsAllowed: false, settingsReady: false, openSettings: () => undefined });

interface AdPrivacyProviderProps {
  children: React.ReactNode;
  config?: AdsConfig;
  bootstrap?: typeof loadPrivacyMessage;
}

export function AdPrivacyProvider({ children, config = adsConfig, bootstrap = loadPrivacyMessage }: AdPrivacyProviderProps) {
  const [adsAllowed, setAdsAllowed] = React.useState(false);
  const [settingsReady, setSettingsReady] = React.useState(false);
  const revokeRequested = React.useRef(false);
  const previouslyAllowed = React.useRef(false);
  React.useEffect(() => {
    setAdsAllowed(false);
    setSettingsReady(false);
    revokeRequested.current = false;
    previouslyAllowed.current = false;
    if (window.adsbygoogle) window.adsbygoogle.pauseAdRequests = 1;
    if (!canBootstrapAds(config) || !isLiveAdsOrigin(window.location.origin) || navigator.globalPrivacyControl) return;
    let active = true;
    let stopObserving: (() => void) | undefined;
    const connect = () => {
      if (!active || stopObserving || !window.__tcfapi) return;
      stopObserving = observeTcfConsent(window.__tcfapi, allowed => {
        if (!active) return;
        if (!allowed && previouslyAllowed.current) revokeRequested.current = true;
        if (allowed) previouslyAllowed.current = true;
        const permitted = !revokeRequested.current && allowed;
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.pauseAdRequests = permitted ? 0 : 1;
        setAdsAllowed(permitted);
      }, () => {
        if (active) setSettingsReady(typeof window.googlefc?.showRevocationMessage === 'function');
      });
    };
    window.googlefc = window.googlefc || { callbackQueue: [] as Array<Record<string, () => void>> };
    const queue = window.googlefc.callbackQueue = window.googlefc.callbackQueue || ([] as Array<Record<string, () => void>>);
    const readyCallback = { CONSENT_API_READY: connect };
    queue.push(readyCallback);
    void bootstrap(config).then(connect, () => {
      if (active) {
        revokeRequested.current = true;
        if (window.adsbygoogle) window.adsbygoogle.pauseAdRequests = 1;
        setAdsAllowed(false);
        setSettingsReady(false);
      }
    });
    return () => {
      active = false;
      stopObserving?.();
    };
  }, [config, bootstrap]);

  const openSettings = React.useCallback(() => {
    setAdsAllowed(false);
    if (window.adsbygoogle) window.adsbygoogle.pauseAdRequests = 1;
    // Stop requests for this document. Updated consent takes effect on reload;
    // do not re-request the same placement when the message opens/closes.
    revokeRequested.current = true;
    const api = window.googlefc;
    if (api?.showRevocationMessage) api.callbackQueue.push({ CONSENT_API_READY: () => api.showRevocationMessage?.() });
  }, []);
  return <PrivacyContext.Provider value={{ adsAllowed, settingsReady, openSettings }}>{children}</PrivacyContext.Provider>;
}

export function useAdPrivacy() { return React.useContext(PrivacyContext); }
export function PrivacySettingsButton({ className = '' }: { className?: string }) {
  const { settingsReady, openSettings } = useAdPrivacy();
  if (!settingsReady) return <a href="/privacy/#privacy-choices" className={className}>Privacy choices</a>;
  return <button type="button" onClick={openSettings} className={className}>Privacy &amp; cookie settings</button>;
}
